import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Truck, Shield, RefreshCw, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { useApp } from '../context/AppContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function HomePage() {
  const { API_URL: apiUrl } = useApp();
  usePageTitle('Smart Choices, True Value');
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API_URL}/products?featured=true&limit=8`),
          axios.get(`${API_URL}/categories`),
          axios.get(`${API_URL}/testimonials?limit=3`)
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl]);

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API_URL}/seed`);
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/products?featured=true&limit=8`),
          axios.get(`${API_URL}/categories`)
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        // Already seeded
      }
    };
    seedData();
  }, []);

  const categoryImages = {
    "Sleepwear": "https://images.unsplash.com/photo-1616627561950-9f746e330187?w=600",
    "Nursing & Breastfeeding": "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=600",
    "Maternity": "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600",
    "Accessories": "https://images.unsplash.com/photo-1522771930-78848d9293e8?w=600"
  };

  return (
    <div className="page-transition">
      {/* Hero Section - Clean & Modern */}
      <section className="relative bg-gradient-to-br from-gray-50 to-gray-100" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center py-12 lg:py-20">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <span className="inline-block px-4 py-1.5 bg-[#EA580C]/10 text-[#EA580C] rounded-full text-sm font-medium mb-4">
                New Arrivals
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quality Products,<br />
                <span className="text-[#EA580C]">Delivered to You</span>
              </h1>
              <p className="text-gray-600 text-lg mb-8 max-w-lg">
                Discover our curated collection of premium products. Quality you can trust, prices you'll love.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/shop" data-testid="hero-shop-btn">
                  <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white px-8 py-6 text-lg font-medium rounded-lg">
                    Shop Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/shop?featured=true">
                  <Button variant="outline" className="px-8 py-6 text-lg font-medium rounded-lg border-2">
                    View Featured
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden bg-white shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800"
                    alt="Shop VShoppe"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 hidden sm:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Truck className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Free Shipping</p>
                      <p className="text-sm text-gray-500">On orders $100+</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex items-center justify-center sm:justify-start gap-4 py-2">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Truck className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Free NZ Shipping</h3>
                <p className="text-sm text-gray-500">On orders over $100</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 py-2">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Easy Returns</h3>
                <p className="text-sm text-gray-500">30-day return policy</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-end gap-4 py-2">
              <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Secure Checkout</h3>
                <p className="text-sm text-gray-500">Stripe & PayPal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Shop by Category
              </h2>
              <p className="text-gray-500 mt-1">Browse our product categories</p>
            </div>
            <Link to="/shop" className="hidden sm:flex items-center text-[#EA580C] font-medium hover:underline">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {categories.map((category) => (
              <Link 
                key={category.id}
                to={`/shop?category=${encodeURIComponent(category.name)}`}
                className="group relative aspect-[4/5] rounded-xl overflow-hidden bg-gray-100"
                data-testid={`category-${category.id}`}
              >
                <img
                  src={categoryImages[category.name] || category.image || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400'}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <h3 className="text-white text-lg sm:text-xl font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {category.name}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {category.product_count} products
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-gray-50" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Featured Products
              </h2>
              <p className="text-gray-500 mt-1">Handpicked just for you</p>
            </div>
            <Link to="/shop" className="hidden sm:flex items-center text-[#EA580C] font-medium hover:underline" data-testid="view-all-link">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200 rounded-xl mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {featuredProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Link to="/shop">
              <Button variant="outline" className="rounded-lg">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="py-16 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Join the VShoppeNZ Family
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Sign up for exclusive offers, early access to new arrivals, and updates on your favorite products.
            </p>
            <Link to="/login">
              <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white px-8 py-6 text-lg font-medium rounded-lg">
                Create Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-16 bg-white" data-testid="home-testimonials">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                What Our Customers Say
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-[#FFFBF5] rounded-2xl border border-border p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className={`h-5 w-5 ${star <= t.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 mb-4 leading-relaxed">
                    "{t.message}"
                  </blockquote>
                  <p className="font-semibold text-gray-900">{t.customer_name}</p>
                  {t.location && <p className="text-gray-500 text-sm">{t.location}</p>}
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/reviews">
                <Button variant="outline" className="rounded-full" data-testid="see-all-reviews-btn">
                  Read all reviews & leave yours
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
